import { useUser } from "@clerk/clerk-react";
import { cn } from "cn";
import type { ReactNode } from "react";
import { CircularProgressbar } from "@/components/ui/circular-progressbar";
import {
  MyPlaceContextProvider,
  OnboardingLayout,
  SupportChatIdentify,
  useMyPlaceContext,
} from "@/features/my/shell";
import { usePresaleView } from "./_api/use-presale-view";
import { PresaleHelp } from "./_components/presale-help";
import { PresaleHero } from "./_components/presale-hero";
import { PresaleNextSteps } from "./_components/presale-next-steps";
import { PresalePublicShell } from "./_components/presale-public-shell";
import {
  PRESALE_REFERRAL_SECTION_ID,
  PresaleReferral,
} from "./_components/presale-referral";

/** The one loading treatment every branch of this page shares. */
function PresaleLoading({ fullScreen }: { fullScreen?: boolean }) {
  return (
    <div
      className={cn(
        /* sorted */
        "flex flex-1 items-center justify-center",
        fullScreen && "min-h-svh",
      )}
    >
      <CircularProgressbar
        value={25}
        className="size-10 animate-spin text-text-primary duration-400"
      />
    </div>
  );
}

function scrollToReferral() {
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  document.getElementById(PRESALE_REFERRAL_SECTION_ID)?.scrollIntoView({
    behavior: prefersReducedMotion ? "auto" : "smooth",
    block: "start",
  });
}

function PresaleContent() {
  const { data: view } = usePresaleView();

  if (!view) {
    return <PresaleLoading />;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-138 flex-col px-6 pt-12 pb-24">
        <PresaleHero view={view} onReferClick={scrollToReferral} />
        <PresaleNextSteps placeLabel={view.placeLabel} />
        <PresaleReferral view={view} />
        <PresaleHelp />
      </div>
    </div>
  );
}

/**
 * The member header only fits a member who actually has a place: `OnboardingLayout`'s
 * place-switcher drawer force-navigates a place-less member to `/my/places`, and the
 * switcher pill would render a permanent skeleton. Everyone else keeps the logo shell.
 */
function PresaleMemberShell({ children }: { children: ReactNode }) {
  const { places, isLoading } = useMyPlaceContext();

  if (isLoading) {
    return <PresaleLoading fullScreen />;
  }

  if (places.length === 0) {
    return <PresalePublicShell>{children}</PresalePublicShell>;
  }

  return <OnboardingLayout>{children}</OnboardingLayout>;
}

/**
 * Founding-member confirmation at `/presale/success` — where a completed presale checkout
 * lands, standing in for `/my/onboarding` for presale places until launch. A PUBLIC page
 * (no sign-in required): a visitor gets a logo-only shell, a signed-in member with a place
 * gets the onboarding header (place switcher + user menu), mounted here because the page
 * lives outside the `/my` shell.
 */
export function PresaleSuccessPage() {
  const { isSignedIn, isLoaded } = useUser();

  if (!isLoaded) {
    return <PresaleLoading fullScreen />;
  }

  if (!isSignedIn) {
    return (
      <PresalePublicShell>
        <PresaleContent />
      </PresalePublicShell>
    );
  }

  return (
    <MyPlaceContextProvider>
      <SupportChatIdentify>
        <PresaleMemberShell>
          <PresaleContent />
        </PresaleMemberShell>
      </SupportChatIdentify>
    </MyPlaceContextProvider>
  );
}
